import axios from 'axios';
import router from '@/router';

import { inject } from 'vue';
import { useAuthStore } from "@/stores/auth";
import { useRouterStore } from "@/stores/router";
import { useFormulariosStore } from "@/stores/formularios";
import { authEndpoint } from "@/api/endpoints/auth";
import { trackAxiosInstance } from "@/commons/networkActivity";
import { useRuntimeConfig } from "@/composables/useRuntimeConfig";

function isCanceledError(error) {
    if (!error) return false;
    if (axios.isCancel?.(error)) return true;
    return error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.code === 'ERR_ABORTED';
}

const { apiBaseUrl, moduloFormulario } = useRuntimeConfig();

const CODIGO_BLOQUEANTE_PENDENTE = 'E205020';

export const useAxios = () => inject('axios');

let isRefreshing = false;
let refreshQueue = [];

// Uma tela costuma disparar vários endpoints de uma vez; sem permissão, todos
// voltam 401. Só o primeiro encerra a sessão e avisa o usuário — os seguintes
// são marcados como silenciados para não empilhar toasts iguais.
let sessaoJaEncerrada = false;

function processQueue(error, token = null) {
    refreshQueue.forEach((item) => {
        if (error) {
            item.reject(error);
        } else {
            item.resolve(token);
        }
    });
    refreshQueue = [];
}

export default {
    install: (app, options) => {
        const axiosInstance = axios.create({
            baseURL: options.baseURL,
            headers: {
                'Accept': 'application/json',
            }
        });
        const authStore = useAuthStore();
        const routerStore = useRouterStore();

        trackAxiosInstance(axiosInstance);

        axiosInstance.interceptors.request.use(
            function (config) {
                const token = authStore.getToken()
                if (token) {
                    // Há token de novo: a sessão anterior acabou e o bloqueio de
                    // avisos daquela queda não vale mais
                    sessaoJaEncerrada = false;
                    config.headers["Authorization"] = 'Bearer ' + authStore.getToken()
                }
                return config
            },
            function (error) {
                return Promise.reject(error);
            }
        );
        axiosInstance.interceptors.response.use(
            function (response) {
                return response
            },
            function (error) {
                if (isCanceledError(error)) {
                    return new Promise(() => { /* silenced: scope canceled */ });
                }

                if (!error.response) {
                    return Promise.reject(error);
                }

                const status = error.response.status;
                const detail = error.response.data?.detail;

                // Formulário bloqueante pendente: a API recusa as demais rotas até ele
                // ser respondido. Leva para a tela de obrigatórios e silencia o erro,
                // que não é falha da tela que fez a chamada.
                if (moduloFormulario.value && status === 403 && error.response.data?.error?.status_code === CODIGO_BLOQUEANTE_PENDENTE) {
                    const formulariosStore = useFormulariosStore();
                    formulariosStore.definirBloqueantes(error.response.data.error.detalhes?.pendencias);

                    const rotaAtual = router.currentRoute.value;
                    if (!rotaAtual.path.startsWith('/formularios')) {
                        formulariosStore.definirRotaRetorno(rotaAtual.fullPath);
                        router.push({ name: 'formulariosObrigatorios' });
                    }

                    error.erroSilenciado = true;
                    return Promise.reject(error);
                }
                const isTokenExpirado = status === 403 && detail === 'Token expirado';

                if (
                    typeof router.options.history.state.back === 'string' &&
                    (router.options.history.state.back.includes('?redirect=true') || router.options.history.state.back.includes('&redirect=true'))
                ) {
                    if (status === 401) {
                        const previousRoute = router.options.history.state.back;
                        routerStore.definirRotaPreLogin(previousRoute)
                    }
                } else {
                    routerStore.definirRotaPreLogin(null)
                }

                // No login (credenciais e 2FA) o 400/401 significa senha/código inválidos,
                // não sessão encerrada: derrubar o usuário aqui impediria a própria
                // autenticação e, pior, deixaria `sessaoJaEncerrada` ligada — como não há
                // token para o interceptor de request zerar a marca, as tentativas
                // seguintes viriam silenciadas e sem toast.
                const ehRequisicaoAutenticacao = error.config?.url?.includes(authEndpoint.login);

                if (!isTokenExpirado) {
                    if ((status === 400 || status === 401) && !ehRequisicaoAutenticacao) {
                        // Rejeitar uma string descartava o corpo da resposta e a tela
                        // só conseguia exibir uma mensagem genérica. O erro segue
                        // inteiro, marcado, para o toast usar a mensagem do backend.
                        error.sessaoEncerrada = true;
                        error.erroSilenciado = sessaoJaEncerrada;

                        if (!sessaoJaEncerrada) {
                            sessaoJaEncerrada = true;
                            authStore.logout();
                            router.push({ name: 'login' });
                        }

                        return Promise.reject(error);
                    }
                    return Promise.reject(error)
                }

                // Token expirado: enfileira ou aguarda o refresh em andamento
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        refreshQueue.push({ resolve, reject });
                    }).then((token) => {
                        error.config.headers["Authorization"] = 'Bearer ' + token;
                        return axiosInstance(error.config);
                    }).catch((err) => {
                        return Promise.reject(err);
                    });
                }

                isRefreshing = true;

                return axios.post(authEndpoint.refreshToken,
                    { "refresh_token": authStore.getRefreshToken() },
                    {
                        baseURL: apiBaseUrl.value,
                        headers: { 'Accept': 'application/json', }
                    }
                ).then((response) => {
                    if (response.status == 200) {
                        const newToken = response.data.access_token;
                        authStore.setToken(newToken);
                        authStore.setRefreshToken(response.data.refresh_token);
                        processQueue(null, newToken);
                        error.config.headers["Authorization"] = 'Bearer ' + newToken;
                        return axiosInstance(error.config);
                    }
                }).catch((refreshError) => {
                    processQueue(refreshError, null);
                    authStore.logout();
                    router.push({ name: 'login' });
                    return Promise.reject(refreshError);
                }).finally(() => {
                    isRefreshing = false;
                });
            }
        );
        app.provide('axios', axiosInstance);
        app.config.globalProperties.$axios = axiosInstance
    }
}